/**
 * LineChart - Time series visualization
 *
 * Usage:
 *   <LineChart
 *     title="Steps over time"
 *     data={{ series: [{ date: '2024-01-01', value: 8241 }, ...] }}
 *   />
 *
 *   // Multi-series
 *   <LineChart
 *     title="Sleep vs Exercise"
 *     data={{
 *       multiSeries: [{ date: '2024-01-01', sleep: 7.2, exercise: 45 }, ...],
 *       seriesNames: ['sleep', 'exercise']
 *     }}
 *   />
 */

import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { BlockProps, LineChartData } from './types';
import { CHART_COLORS, getSeriesColor } from './colors';
import './blocks.css';

export interface LineChartProps extends BlockProps<LineChartData> {
  /** Chart height in pixels */
  height?: number;
  /** Show dots on data points */
  showDots?: boolean;
}

export function LineChart({
  data,
  title,
  config,
  height = 250,
  showDots = true,
  className,
}: LineChartProps) {
  const { series, multiSeries, seriesNames } = data;

  // Normalize data to multi-series format
  const chartData = multiSeries || series?.map(point => ({
    date: point.date,
    value: point.value,
  }));

  if (!chartData || chartData.length === 0) {
    return (
      <div className={`block-chart ${className || ''}`}>
        {title && <div className="block-chart-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  // Determine which keys to plot
  const keysToPlot = seriesNames || (series ? ['value'] : Object.keys(chartData[0]).filter(k => k !== 'date'));

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    return (
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '6px',
        padding: '8px 12px',
        boxShadow: 'var(--popover-shadow, 0 2px 4px rgba(0,0,0,0.1))',
      }}>
        <p style={{
          color: 'var(--color-text-subtle)',
          fontWeight: 600,
          margin: '0 0 8px 0',
          fontSize: '13px',
        }}>
          {formatDate(label)}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{
            color: entry.color,
            margin: '4px 0',
            fontSize: '14px',
          }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className={`block-chart ${className || ''}`}>
      {title && <div className="block-chart-title">{title}</div>}

      <div className="block-chart-container">
        <ResponsiveContainer width="100%" height={height}>
          <RechartsLineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
            <CartesianGrid
              strokeDasharray="0"
              stroke="var(--color-border)"
              strokeWidth={1}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              stroke="var(--color-text-faint)"
              tick={{ fill: 'var(--color-text-faint)', fontSize: 10 }}
              axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
              tickLine={false}
              tickFormatter={formatDate}
            />
            <YAxis
              stroke="var(--color-text-faint)"
              tick={{ fill: 'var(--color-text-faint)', fontSize: 10 }}
              axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
              tickLine={false}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip content={<CustomTooltip />} />
            {keysToPlot.length > 1 && <Legend />}

            {keysToPlot.map((key, index) => {
              const color = config?.colors?.[index] || getSeriesColor(index);
              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={key.replace(/_/g, ' ')}
                  stroke={color}
                  strokeWidth={2}
                  dot={showDots ? { fill: color, strokeWidth: 2, r: 3 } : false}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              );
            })}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default LineChart;
